import { useEffect, useState } from 'react'
import { streamOpen } from './commandStream'
import { fetchServers, type PairedServer, type ServerList } from './serverList'
import { relativeAge } from '../ui/relativeTime'
import { Icon } from '../ui/Icon'

/**
 * Your machines, as content.
 *
 * This was the body of a modal, and a modal was the wrong container: choosing a
 * machine is not a detour from the screen, it IS the screen. A dialog has a close
 * button, and closing this one left a blank page with a single button on it — which
 * is what the owner saw. Content that cannot be dismissed cannot strand anyone.
 *
 * Five states, none borrowing another's words:
 *
 *   servers      pick one -> onPick
 *   none         paired nothing yet -> point at QR pairing, not an empty list
 *   unreachable  the BFF did not answer -> show the detail, offer retry
 *   unwired      dev-only: the contract is not connected, and it says so plainly
 *   loading      null, which is not the same as "no servers"
 *
 * ── PROMOTED 2026-09-02 (owner: 做) ──────────────────────────────────────────
 *
 * This rendered nowhere: its only mount was src/servers/Workspace.tsx, a screen reachable
 * from nothing but its own test. Meanwhile the ＋ sheet's machine picker was a `<select>`
 * that re-implemented these same five states — and said so in its own doc comment, which
 * is how the duplication was found. Deleting this file would have made that permanent, so
 * it is the single renderer of a machine list now and the `<select>` is gone.
 *
 * IT DOES NOT RETARGET THE RELAY ANY MORE. The old version called setRelayTarget() inside
 * onPick, because on the screen it was written for, picking a machine WAS choosing where
 * every request goes. That is not true of the ＋ sheet, where picking names where an agent
 * will be created and the sheet keeps the choice in its own state. A component that
 * changes global routing as a side effect of being rendered somewhere new is a trap, so
 * the caller decides: this reports the pick and nothing else.
 */
/** How many agents a machine has, from its list when it sent one, else its count; null when unknown. */
function agentCount(s: PairedServer): number | null {
  if (s.agents !== null) return s.agents.length
  return typeof s.agent_count === 'number' ? s.agent_count : null
}

export function MachineList({
  onPick,
  selected,
  onClear,
  open,
}: {
  onPick: (server: PairedServer) => void
  /** server_id of the current choice, so the row can show it. Optional: a screen that
   *  navigates away on pick has nothing to highlight. */
  selected?: string | null
  /**
   * Clear the choice and show the whole list again. When given, a chosen machine
   * COLLAPSES the list to just itself with an × back to the rest — the full list
   * standing over the config below was the complaint (owner, 2026-09-03). Omitted by
   * a caller that has no "previous state" to return to.
   */
  onClear?: (() => void) | undefined
  /**
   * A button at the right of every card that DOES the thing on that machine — owner,
   * 2026-10-18 (＋ → Terminal): 只需要在電腦卡片的右邊增加一個 Open 的按鈕就好了, 這樣子最方便.
   * With it the card is a label and the button its one control (no pick, then a button
   * below). `busy` names the machine being acted on; every button waits while one is.
   */
  open?: { label: string; busyLabel: string; busy: string | null; onOpen: (server: PairedServer) => void } | undefined
}) {
  const [list, setList] = useState<ServerList | null>(null)

  const load = () => {
    setList(null)
    void fetchServers().then(setList)
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [])

  // A chosen machine hides the others; the × (only worth showing when there is more
  // than one) returns to the full list.
  const servers = list?.kind === 'servers' ? list.servers : []
  const sel = selected ? servers.find((s) => s.server_id === selected) : undefined
  const rows = sel ? [sel] : servers

  return (
    <>
    {list === null ? (
      <p className="gh-note">Looking for your servers…</p>
    ) : list.kind === 'servers' ? (
        <div className="svr-list">
          {/* The way back from a collapsed list — above the chosen machine, as asked. */}
          {sel && onClear && servers.length > 1 && (
            <button type="button" className="svr-clear" onClick={onClear}>
              <Icon name="close" size={14} className="svr-clear-x" />
              Choose a different machine
            </button>
          )}
          {/*
            * Say when this is not live.
            *
            * The first frame comes over HTTP, so a socket that never opens shows a
            * list rather than a blank — but then stale and current look exactly the
            * same, which is the failure this whole day has been about. A machine
            * that went offline a minute ago still reads as online until something
            * says otherwise, so the screen has to admit when nothing can.
            *
            * Only in the full list: once a machine is chosen the liveness caveat is
            * about a choice already made, and the collapsed view is meant to be just
            * the machine.
            */}
          {!sel && !streamOpen() && (
            <p className="gh-note svr-notlive" role="status">
              Not live — this is the last thing we heard, not what is happening now.
            </p>
          )}
        {rows.map((s) => {
          const Row = open ? 'div' : 'button'
          return (
          <div key={s.server_id} className="svr-rowwrap">
          <Row
            {...(open
              ? { className: 'svr-row svr-static' }
              : {
                  type: 'button' as const,
                  className: `svr-row${selected === s.server_id ? ' on' : ''}`,
                  'aria-pressed': selected === undefined ? undefined : selected === s.server_id,
                  onClick: () => onPick(s),
                })}
            // Selectable even when offline/unknown: the user may want to see it,
            // and the CREATE step is where an offline server refuses — with its
            // own words, not by a greyed row that never explains itself.
          >
            <span className={`svr-dot ${s.reachable}`} aria-hidden="true" />
            <span className="svr-name">
              {s.name}
              {/*
                * THE DOT AND THE WORD COME FROM ONE FIELD. They did not: the dot read
                * `reachable` (online / offline / unknown, decided in serverList.ts against
                * the beat contract) while the text beside it said "online" whenever
                * last_seen_age_s <= 60 on its own. A machine the account service reports
                * as live:false but which beat 30s ago rendered a RED DOT NEXT TO THE WORD
                * ONLINE — one row contradicting itself, from two sources for one fact.
                *
                * The age is still shown, as what it actually is: detail under the verdict,
                * never a second opinion about it. `unknown` stays unworded — on this
                * deployment the webapp's BFF instance never holds a machine's link, so it
                * is the steady state, and a word on every row carries no information.
                */}
              <span className="svr-liveness">
                {[
                  // `unknown` stays unworded: on this deployment the webapp's BFF instance
                  // never holds a machine's link, so it is the steady state, and a word on
                  // every row carries no information.
                  s.reachable === 'unknown' ? null : s.reachable,
                  s.last_seen_age_s === null
                    ? 'never connected'
                    : s.last_seen_age_s <= 60
                      ? 'seen just now'
                      : `last seen ${relativeAge(s.last_seen_age_s)}`,
                ]
                  // JOINED, not conditionally separated. The first version emitted the
                  // separator from its own condition and the two disagreed: a machine that
                  // was offline and had never connected rendered "offlinenever connected".
                  // Found by looking at the screenshot, not by a test.
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
            {/*
              * COUNTED, NOT NAMED (owner, 2026-10-27, over a phone's machine sheet where every
              * card carried a line of agent names, cut off at two lines: 現在我的機器的list已經
              * 過長了 … 在選擇機器的時候只要看到機器的名稱以及上面有幾個Agent就好了). The names
              * were there to answer "where did the agent I just started go" — the rail answers
              * that now, machine by machine. Null stays blank rather than rendering as "0 agents",
              * which would report running agents as absent.
              */}
            {agentCount(s) !== null && (
              <span className="svr-count">
                {agentCount(s)} agent{agentCount(s) === 1 ? '' : 's'}
              </span>
            )}
            {open && (
              <button
                type="button"
                className="svr-open"
                disabled={open.busy !== null}
                aria-label={`${open.label} on ${s.name}`}
                onClick={() => open.onOpen(s)}
              >
                {open.busy === s.server_id ? open.busyLabel : open.label}
              </button>
            )}
            </Row>
            </div>
          )
        })}
      </div>
    ) : list.kind === 'none' ? (
      <>
        <p className="gh-note">No servers are linked to this account yet.</p>
        <p className="gh-note">
          Install the server on your machine, then scan the QR code it prints —
          that links it here.
        </p>
      </>
    ) : list.kind === 'unreachable' ? (
      <>
        <p className="gh-note">Could not fetch your servers.</p>
        <p className="svr-detail">{list.detail}</p>
        <button type="button" className="sheet-btn" onClick={load}>
          Try again
        </button>
      </>
    ) : (
      /* unwired — development only, and honest about it. "No servers" here would
         be a confident answer to a question nothing has asked yet. */
      <p className="gh-note">
        The server list is not connected to the backend yet — the pairing contract
        is still being defined.
      </p>
    )}
    </>
  )
}
